
import { Page, PageId } from '@/models/page'
import { PrefetchTableFilters } from '@/models/app-data'
import { Resource, ResourceTypeLinkMapping, ResourceURL } from '@/models/resource'
import { Component, Prop, Vue, Ref } from 'vue-property-decorator'
import { MDCDialog } from '@material/dialog'
import { MDCMenu } from '@material/menu'
import ChipMultiSelect from './chip-multi-select.vue'

@Component({
  components: {
    ChipMultiSelect
  }
})
export default class PrefetchTable extends Vue {
  @Prop() pages!: Page[]
  @Prop() private resources!: Resource[]
  @Ref() dialog!: HTMLElement
  @Ref() exportMenu!: HTMLElement

  // Material component reference
  private dialogMDC: MDCDialog | null = null
  private exportMenuMDC: MDCMenu | null = null

  filters: PrefetchTableFilters = {
    // whether un-selected rows should be hidden from the UI (default: shown, but grayed-out)
    collapseUnselected: false,
    // whether URLs shall be displayed shortened (example.com/.../file.js) in the table (default: shorten)
    shortenURLs: true,
    resourceTypes: {
      script: true,
      stylesheet: true,
      document: true,
      font: true
    }
  }

  generated = {
    // holds generated output as requested, e.g.
    // WPT script or prefetch HTML output
    prefetchExport: ''
  }

  get prefetchList(): Resource[] {
    const list = this.resources
      .filter((resource: Resource) => this.filters.resourceTypes[resource.resourceType])
      // filter out resources already requested on page 1 (prefetch obsolete as it has no effect)
      .filter((resource: Resource) => !resource.pages.has(this.pages[0].id))
      // mark page for prefetching
      .map((resource) => {
        for (let i = 1; i < this.pages.length; i++) {
          if (resource.pages.has(this.pages[i].id)) {
            resource.prefetchOn = this.pages[i - 1].id
            break
          }
        }
        return resource
      })
      // sort by size DESC
      .sort((a, b) => (a.size < b.size ? 1 : a.size > b.size ? -1 : 0))
      // sort by prefetching path ASC
      .sort((a, b) => {
        // TODO: quickfix to get it working for now with TS nullable type check
        if (!a.prefetchOn || !b.prefetchOn) return 0
        // TODO: sorting on ID is fragile, better use Page.order property
        return a.prefetchOn > b.prefetchOn ? 1 : a.prefetchOn < b.prefetchOn ? -1 : 0
      })
    return list
  }

  get selectedPrefetchList(): Resource[] {
    return this.prefetchList.filter((asset) => asset.selectedPrefetch)
  }

  get pagesObject(): Record<PageId, Page> {
    const pagesObject: Record<PageId, Page> = {}
    this.pages.forEach((page) => (pagesObject[page.id] = page))
    return pagesObject
  }

  get selectedPrefetchAssetsByPage(): Record<PageId, Resource[]> {
    const assetsByPage: Record<PageId, Resource[]> = {}
    this.pages.forEach((page) => (assetsByPage[page.id] = []))
    this.selectedPrefetchList.forEach((asset) => {
      if (asset.prefetchOn) {
        assetsByPage[asset.prefetchOn].push(asset)
      }
    })
    return assetsByPage
  }

  get totalAssets(): number {
    return this.selectedPrefetchList.length
  }

  get totalSize(): string {
    return this.formatSize(this.selectedPrefetchList.reduce((sum: number, current: Resource) => (sum += current.size), 0))
  }

  // METHODS

  mounted() {
    if (this.dialog) {
      this.dialogMDC = new MDCDialog(this.dialog)
    }
    if (this.exportMenu) {
      this.exportMenuMDC = new MDCMenu(this.exportMenu)
    }
  }

  openPageSettings() {
    this.$emit('openPageSettings')
  }

  openExportMenu() {
    if (this.exportMenuMDC) {
      this.exportMenuMDC.open = true
    }
  }

  // readable file size
  formatSize(size: number): string {
    if (size > 1000000) {
      return `${Math.round((size / 1000000) * 10) / 10} MiB`
    } else if (size > 1000) {
      return `${Math.round(size / 1000)} KiB`
    }
    return `${size} B`
  }

  // render prefetch entry per page/asset
  prefetchTask(page: Page, asset: Resource): string {
    if (page.id === asset.prefetchOn) {
      return '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M16 10H6.83L9 7.83l1.41-1.41L9 5l-6 6 6 6 1.41-1.41L9 14.17 6.83 12H16c1.65 0 3 1.35 3 3v4h2v-4c0-2.76-2.24-5-5-5z"/></svg>'
    } else if (asset.pages.has(page.id)) {
      return this.formatPercentage(Math.ceil((asset.size / page.size) * 1000) / 10)
    }
    return '—'
  }

  // get total saved transfer size for a given page (via PageId)
  totalPrefetchSavings(pageId: PageId): number {
    return this.prefetchList
      .filter((resource) => resource.selectedPrefetch && resource.pages.has(pageId))
      .reduce((sum, current) => (sum += current.size), 0)
  }

  // get total savings for prefetching selected resources in percent
  totalPrefetchSavingsPercentage(pageId: PageId) {
    return Math.round((this.totalPrefetchSavings(pageId) / this.pagesObject[pageId].size) * 1000) / 10
  }

  formatPercentage(percentValue: number): string {
    return `<span class="percentValue" style="opacity: ${percentValue / 100 + 0.5}">${percentValue}%</span>`
  }

  getDisplayedURL(url: ResourceURL): string {
    // do not shorten short URLs
    if (!this.filters.shortenURLs || url.length < 65) {
      return url
    }
    const splittedProtocol = url.split('://')
    const splittedPath = splittedProtocol[1].split('/')

    // remove empty element at the end for URLs ending with '/'
    if (splittedPath[splittedPath.length - 1] === '') {
      splittedPath.splice(-1)
    }

    let shortenedURL = ''

    // do not shorten paths with one sub-directory
    if (splittedPath.length < 3) {
      shortenedURL = url
    }

    shortenedURL = `${splittedProtocol[0]}://${splittedPath[0]}/…/${splittedPath[splittedPath.length - 1]}`

    // final check for too long URLs
    if (shortenedURL.length > 100) {
      shortenedURL = shortenedURL.substr(0, 100) + '…'
    }

    return shortenedURL
  }

  generatePrefetchHTML() {
    this.generated.prefetchExport = ''
    let html = ''

    for (const [pageId, assets] of Object.entries(this.selectedPrefetchAssetsByPage)) {
      if (assets.length) {
        html += `${this.pagesObject[pageId].label} (${this.pagesObject[pageId].url}):
----------------------
`
        let asAttribute = ''
        assets.forEach((asset) => {
          asAttribute = asset.resourceType in ResourceTypeLinkMapping ? ` as="${ResourceTypeLinkMapping[asset.resourceType]}"` : ''
          html += `<link rel="prefetch" href="${asset.url}"${asAttribute}>
`
        })
        html += `

`
      }
    }
    this.generated.prefetchExport = html

    if (this.dialogMDC) {
      this.dialogMDC.open()
    }
  }

  generateWPTScript() {
    this.generated.prefetchExport = ''
    let html = `// NO PREFETCHING
// ----------------------
// Script start
`
    this.pages.forEach(
      (page) =>
        (html += `setEventName\t${page.label}
navigate\t${page.url}

`)
    )
    html += `// Script end

// WITH PREFETCHING
// ----------------------
// Script start
`
    for (const [pageId, assets] of Object.entries(this.selectedPrefetchAssetsByPage)) {
      html += `setEventName\t${this.pagesObject[pageId].label}
navigate\t${this.pagesObject[pageId].url}

`
      if (assets.length) {
        html += 'exec\tvar entries=['
        let asAttribute = ''
        assets.forEach((asset) => {
          asAttribute = asset.resourceType in ResourceTypeLinkMapping ? `,as:"${ResourceTypeLinkMapping[asset.resourceType]}"` : ''
          html += `{rel:"prefetch",href:"${asset.url}"${asAttribute}}`
        })
        html += `];
execAndWait\tentries.map(function(entry) { var link = document.createElement("link"); const hint = Object.assign(link, entry); document.head.append(hint); return;});

`
      }
    }
    html += '// Script end'
    this.generated.prefetchExport = html

    if (this.dialogMDC) {
      this.dialogMDC.open()
    }
  }
}
